import { useCallback, useEffect, useRef, useState } from "react";
import { ME_COINS, ME_HAND, TABLE_CENTER, seatAnchor } from "./fxAnchors";
import { createId } from "./id";
import type { GameState, Role } from "./types";

/**
 * VEIL has no event stream. Every mutation on the server ends in a single
 * `broadcastState()`, so the client's only signal is a full snapshot — and the
 * game deliberately has no log to read the story out of (decision #1). All
 * narrative therefore has to be recovered by diffing consecutive snapshots,
 * which is what this file does.
 *
 * The one piece of state that is NOT recoverable from the new snapshot is the
 * challenged role: `challengeClaim` resolves inside a single message, and on a
 * successful proof the proven card has already been swapped for a fresh draw.
 * It only survives in the PREVIOUS snapshot's `window.claimRole`.
 */
export type FxEffect =
  | { id: string; kind: "coin"; from: string; to: string; count: number }
  | { id: string; kind: "claim"; anchor: string; playerId: string; role: Role }
  | { id: string; kind: "reveal"; anchor: string; playerId: string; role: Role }
  | { id: string; kind: "out"; anchor: string; playerId: string }
  | {
      id: string;
      kind: "challenge";
      claimantId: string;
      challengerId: string;
      loserId: string;
      role: Role;
      proved: boolean;
    }
  | { id: string; kind: "victory"; winnerId: string | null };

/** Hard ceiling on the live queue, so a burst can never pile up unbounded. */
const MAX_LIVE = 12;
/** More than this many coins in one move and the extra sprites add nothing. */
const MAX_SPRITES = 5;

export interface GameDeltas {
  effects: FxEffect[];
  dismiss: (id: string) => void;
  flush: () => void;
}

export function useGameDeltas(game: GameState | null, meId: string): GameDeltas {
  const previous = useRef<GameState | null>(null);
  const [effects, setEffects] = useState<FxEffect[]>([]);

  useEffect(() => {
    const before = previous.current;
    previous.current = game;
    if (!game) return;
    // Three snapshots have no story in them and would otherwise fire the whole
    // table at once: a cold start, a different room, and the rematch rewind
    // (`restartGame` resets turn to 1 and re-deals every hand).
    if (!before || before.code !== game.code) return;
    if (game.turn < before.turn) return;
    if (before.phase === "game_over" && game.phase !== "game_over") return;

    const next = readDeltas(before, game, meId);
    if (next.length) setEffects((live) => [...live, ...next].slice(-MAX_LIVE));
  }, [game, meId]);

  const dismiss = useCallback((id: string) => {
    setEffects((live) => live.filter((effect) => effect.id !== id));
  }, []);

  const flush = useCallback(() => setEffects([]), []);

  return { effects, dismiss, flush };
}

function readDeltas(before: GameState, after: GameState, meId: string): FxEffect[] {
  const out: FxEffect[] = [];
  const was = new Map(before.players.map((player) => [player.id, player]));
  // You have no seat pill — PlayerRail filters you out — so your coin badge
  // stands in for your seat, and your hand stands in for your body.
  const purse = (playerId: string) => (playerId === meId ? ME_COINS : seatAnchor(playerId));
  const body = (playerId: string) => (playerId === meId ? ME_HAND : seatAnchor(playerId));

  // ---------------------------------------------------------------- coins ---
  const moves = after.players
    .map((player) => {
      const previous = was.get(player.id);
      return previous ? { id: player.id, delta: player.coins - previous.coins } : null;
    })
    .filter((move): move is { id: string; delta: number } => Boolean(move) && move!.delta !== 0);

  const taker = moves.find((move) => move.delta > 0);
  const giver = moves.find((move) => move.delta < 0);
  if (moves.length === 2 && taker && giver && taker.delta === -giver.delta) {
    // A transfer: Steal, and for free the rollback that reverses one.
    out.push({
      id: createId(),
      kind: "coin",
      from: purse(giver.id),
      to: purse(taker.id),
      count: sprites(taker.delta)
    });
  } else {
    for (const move of moves) {
      out.push(
        move.delta > 0
          ? {
              id: createId(),
              kind: "coin",
              from: TABLE_CENTER,
              to: purse(move.id),
              count: sprites(move.delta)
            }
          : {
              // Paying for a Coup or an Assassination.
              id: createId(),
              kind: "coin",
              from: purse(move.id),
              to: TABLE_CENTER,
              count: sprites(-move.delta)
            }
      );
    }
  }

  // ---------------------------------------------------------------- claim ---
  // A card visibly comes out of whoever claims a role. It looks EXACTLY the
  // same whether the claim is true or a bluff — the client knows its own hand
  // and could trivially compute the difference, and any tell would leak it.
  const claims: { playerId: string; role: Role }[] = [];
  const window = after.window;
  if (window?.kind === "claim" && window.claimantId && window.claimRole) {
    const stale = before.window;
    const isNew =
      !stale ||
      stale.kind !== "claim" ||
      stale.claimantId !== window.claimantId ||
      stale.claimRole !== window.claimRole ||
      before.action?.id !== after.action?.id;
    if (isNew) claims.push({ playerId: window.claimantId, role: window.claimRole });
  }
  // A block IS a claim. The server opens the matching claim window in the same
  // broadcast, so the two fire together and have to be deduped.
  if (after.action?.blockRole && after.action.blockerId && !before.action?.blockRole) {
    const block = { playerId: after.action.blockerId, role: after.action.blockRole };
    if (!claims.some((claim) => claim.playerId === block.playerId && claim.role === block.role)) {
      claims.push(block);
    }
  }
  for (const claim of claims) {
    out.push({
      id: createId(),
      kind: "claim",
      anchor: body(claim.playerId),
      playerId: claim.playerId,
      role: claim.role
    });
  }

  // ------------------------------------------------------------ challenge ---
  // `challengeClaim` nulls the window, resolves, and opens the loss all inside
  // one message, so this is the only frame in which the truth is legible.
  if (before.phase === "claim_window" && before.window?.claimRole && before.window.claimantId) {
    const loss = after.pendingLoss;
    const bluffed = loss?.cause.startsWith("Bluffed ");
    const failed = loss?.cause.startsWith("Failed challenge against ");
    if (loss && (bluffed || failed)) {
      const claimantId = before.window.claimantId;
      out.push({
        id: createId(),
        kind: "challenge",
        claimantId,
        // On a bluff the server names the challenger; on a proof it leaves the
        // field null, but the loser IS the challenger, so it is recoverable.
        challengerId: (bluffed ? loss.challengerId : loss.playerId) ?? loss.playerId,
        loserId: loss.playerId,
        role: before.window.claimRole,
        proved: Boolean(failed)
      });
    }
  }

  // ----------------------------------------------------- reveals and outs ---
  for (const player of after.players) {
    const previous = was.get(player.id);
    if (!previous) continue;
    for (const card of player.influence) {
      if (!card.revealed || !card.role) continue;
      const stale = previous.influence.find((old) => old.id === card.id);
      // An id absent from the old hand is a fresh draw or an exchange, never a
      // loss. A card that was already face up is old news.
      if (!stale || stale.revealed) continue;
      out.push({
        id: createId(),
        kind: "reveal",
        anchor: body(player.id),
        playerId: player.id,
        role: card.role
      });
    }
    if (previous.alive && !player.alive) {
      out.push({ id: createId(), kind: "out", anchor: body(player.id), playerId: player.id });
    }
  }

  // -------------------------------------------------------------- victory ---
  if (before.phase !== "game_over" && after.phase === "game_over") {
    out.push({ id: createId(), kind: "victory", winnerId: after.winnerId });
  }

  return out;
}

function sprites(delta: number) {
  return Math.max(1, Math.min(Math.abs(delta), MAX_SPRITES));
}
